export const CAMPAIGN_TABLE_ENTITY_KIND = {
  AD_GROUP: "adGroup",
  ASSETS_GROUP: "assetsGroup",
  AD: "ad",
};

// Loading the campaign tree (campaign > ad groups > ads) and loading its
// statistics are two independent loads: the structure must render as soon as
// it arrives, while metrics keep loading behind per-cell skeletons.
export const CAMPAIGN_TABLE_LOAD_PHASE = {
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
};

export const isCampaignTableLoadSettled = (phase) =>
  phase === CAMPAIGN_TABLE_LOAD_PHASE.LOADING ||
  phase === CAMPAIGN_TABLE_LOAD_PHASE.READY;

// What an expanded campaign subtree should render for a given entry.
// "loader": no entities yet, show the row loader.
// "error": the entity tree failed, show the error row.
// "metrics": the tree is available; render the entities immediately and put
//   skeletons only in the statistics cells while those keep loading.
export const CAMPAIGN_TABLE_TREE_VIEW = {
  LOADER: "loader",
  ERROR: "error",
  METRICS: "metrics",
};

export const getCampaignTableTreeView = (entry) => {
  const campaign = entry?.campaign;
  const treePhase = entry?.treePhase;

  // No entities yet: either the tree is still loading or it failed outright.
  if (!campaign) {
    return treePhase === CAMPAIGN_TABLE_LOAD_PHASE.ERROR
      ? CAMPAIGN_TABLE_TREE_VIEW.ERROR
      : CAMPAIGN_TABLE_TREE_VIEW.LOADER;
  }

  // Entities already known: never replace them with a loader again, not even
  // when a refresh (new date range) is in flight or failed.
  return CAMPAIGN_TABLE_TREE_VIEW.METRICS;
};

// Metrics stay in skeleton only until something settles; on error the cells
// fall back to "-" and a message row explains the failure.
export const isCampaignTableStatisticsLoading = (statsPhase) =>
  statsPhase !== CAMPAIGN_TABLE_LOAD_PHASE.READY &&
  statsPhase !== CAMPAIGN_TABLE_LOAD_PHASE.ERROR;

export const CAMPAIGN_TABLE_ADS_PAGE_SIZE = 25;

// Name-column spacing (MUI theme units). Campaign text starts after its
// chevron. Ad text starts after a 4.5 indent plus the same chevron spacer.
// 2.5 puts the ad-group label between those two, including that spacer.
export const CAMPAIGN_TABLE_NAME_INDENT = {
  AD_GROUP: 2.5,
  AD: 4.5,
};

// Below the campaign row sticky cells (999 / 1001) so an open tree cannot
// paint over that row and swallow its click.
export const CAMPAIGN_TABLE_NESTED_STICKY_Z_INDEX = 998;

export const buildCampaignEditorPath = ({
  campaignID,
  platform,
  adGroupID,
  assetsGroupID,
  adID,
}) => {
  const params = new URLSearchParams();
  if (campaignID) params.set("campaignID", campaignID);
  if (platform) params.set("platform", platform);
  if (assetsGroupID) params.set("assetsGroupID", assetsGroupID);
  if (adGroupID) params.set("adGroupID", adGroupID);
  if (adID) params.set("adID", adID);
  return `/ads-campaign?${params.toString()}`;
};

const compareGroups = (left, right) => {
  const byPlatform = String(left.platform || "").localeCompare(
    String(right.platform || "")
  );
  if (byPlatform !== 0) return byPlatform;
  return String(left.name || "").localeCompare(String(right.name || ""));
};

export const buildCampaignTableGroups = ({ campaign, deletedStatus }) => {
  const groups = [];

  (campaign?.adsGroups || []).forEach((group) => {
    if (!group?.id || group.status === deletedStatus) return;

    groups.push({
      kind: CAMPAIGN_TABLE_ENTITY_KIND.AD_GROUP,
      id: group.id,
      platform: group.platform,
      name: group.name || "",
      status: group.status,
      creationTime: group.creationTime || null,
      ads: (group.ads || []).filter(
        (ad) => ad?.id && ad.status !== deletedStatus
      ),
    });
  });

  (campaign?.assetsGroups || []).forEach((group) => {
    if (!group?.id || group.status === deletedStatus) return;

    groups.push({
      kind: CAMPAIGN_TABLE_ENTITY_KIND.ASSETS_GROUP,
      id: group.id,
      platform: group.platform,
      name: group.name || "",
      status: group.status,
      creationTime: group.creationTime || null,
      ads: [],
    });
  });

  return groups.sort(compareGroups);
};

export const withCampaignTreeEntityStatus = ({
  campaign,
  kind,
  entityID,
  adGroupID,
  status,
}) => {
  if (!campaign) return campaign;

  switch (kind) {
    case CAMPAIGN_TABLE_ENTITY_KIND.AD_GROUP:
      return {
        ...campaign,
        adsGroups: (campaign.adsGroups || []).map((group) =>
          group.id === entityID ? { ...group, status } : group
        ),
      };
    case CAMPAIGN_TABLE_ENTITY_KIND.ASSETS_GROUP:
      return {
        ...campaign,
        assetsGroups: (campaign.assetsGroups || []).map((group) =>
          group.id === entityID ? { ...group, status } : group
        ),
      };
    case CAMPAIGN_TABLE_ENTITY_KIND.AD:
      return {
        ...campaign,
        adsGroups: (campaign.adsGroups || []).map((group) => {
          if (group.id !== adGroupID) return group;
          return {
            ...group,
            ads: (group.ads || []).map((ad) =>
              ad.id === entityID ? { ...ad, status } : ad
            ),
          };
        }),
      };
    default: {
      const exhaustiveKind = kind;
      void exhaustiveKind;
      return campaign;
    }
  }
};

export const getCampaignTableEntityName = ({ entity, fallback }) => {
  const name = String(entity?.name || "").trim();
  if (name) return name;

  const title = entity?.titles?.[0];
  const titleValue =
    typeof title === "string"
      ? title.trim()
      : String(title?.value || "").trim();
  if (titleValue) return titleValue;

  return fallback;
};

// createdBy is only persisted on the campaign: ad groups and ads do not carry
// their own attribution, so nested rows mirror the campaign author by rendering
// the campaign's user. The date is the entity's own creation time when present.
export const getCampaignTableEntityCreationTime = ({ entity, campaign }) =>
  entity?.creationTime || campaign?.creationTime || null;

// date-fns throws RangeError on an invalid Date, and that would take down the
// whole table render. Normalize every timestamp to a valid Date or null so the
// cells can simply render an empty string when there is nothing to show.
export const toCampaignTableDate = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number" && !Number.isFinite(value)) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getCampaignTreeCacheKey = ({ campaignID, dateRange }) => {
  const toToken = (value) => {
    if (value == null || value === "") return "";
    const time =
      value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(time) ? String(time) : "";
  };

  return `${campaignID}|${toToken(dateRange?.startDateTime)}|${toToken(
    dateRange?.endDateTime
  )}`;
};

// Only the statistics depend on the date range. The entity tree does not, so
// entries are keyed by campaignID and the range is stored inside as
// `statisticsKey`: changing the filter refreshes numbers without losing the
// already rendered ad groups and ads.
export const getCampaignTreeStatisticsCacheKey = ({ campaignID, dateRange }) =>
  getCampaignTreeCacheKey({ campaignID, dateRange });
